import { useQuery, useQueryClient, type QueryStatus } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { DBProps, ITool, OmitDBProps, dbProps } from 'types'
import { FetchStatus, getDocs, usePrefCurrentWorkspace } from 'utils'

export const makeEmptyTool = (workspaceId: string): OmitDBProps<ITool> => ({
  workspaceId,
  title: '',
  url: '',
})

export type ToolRecord = Pick<ITool, keyof DBProps | (typeof partialToolFields)[number]>

const partialToolFields = ['title', 'url', 'workspaceId'] as const satisfies (keyof ITool)[]

const makeQueryKey = (workspaceId?: string) => ['tools', workspaceId] as const

export function useTools() {
  const [workspaceId] = usePrefCurrentWorkspace()
  const enabled = !!workspaceId
  const query = useQuery({
    queryKey: makeQueryKey(workspaceId),
    queryFn: () => fetchTools(workspaceId!),
    enabled,
    staleTime: 1000 * 30,
  })

  const tools = query.data ?? []
  const status = getFetchStatus(query.status, tools)

  return { tools, status }
}

export function useMutateToolRecords() {
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()

  async function updateToolRecord(body: ITool) {
    if (!workspace) return

    const record = makeToolRecord(body)

    queryClient.setQueriesData<ToolRecord[]>(
      { queryKey: makeQueryKey(workspace.id) },
      tools => {
        if (!tools) return [record]

        const toolIndex = tools.findIndex(e => e.id === record.id)
        if (toolIndex < 0) return sortToolRecords([...tools, record])

        return sortToolRecords(tools.map((e, i) => (i === toolIndex ? record : e)))
      }
    )
  }

  async function removeToolRecord(id: string) {
    if (!user || !workspace) return

    queryClient.setQueriesData<ToolRecord[]>(
      { queryKey: makeQueryKey(workspace.id) },
      tools => {
        if (!tools) return []

        const toolIndex = tools.findIndex(e => e.id === id)
        if (toolIndex < 0) return tools

        return tools.filter((_, i) => i !== toolIndex)
      }
    )
  }

  return { updateToolRecord, removeToolRecord }
}

async function fetchTools(workspaceId: string) {
  const tools = await getDocs({
    collection: 'tools',
    fields: partialToolFields,
    sort: { by: 'title', order: 'ASC' },
    constraints: [{ field: 'workspaceId', op: '==', value: workspaceId }],
  })

  return sortToolRecords(tools.data.map(makeToolRecord))
}

function makeToolRecord(tool: ToolRecord) {
  return pick(tool, [...dbProps, ...partialToolFields]) as ToolRecord
}

function sortToolRecords(tools: ToolRecord[]) {
  return [...tools].sort((a, b) => a.title.localeCompare(b.title))
}

function getFetchStatus(status: QueryStatus, tools: ToolRecord[]): FetchStatus {
  if (status === 'pending') return 'loading'
  if (status === 'error') return 'error'
  return tools.length ? 'success' : 'empty'
}
